import { useState, useEffect } from 'react';
import { estimateAPI } from '../../services/api';

/**
 * Formats a number as USD currency: $X,XXX.XX
 */
const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '-';
  return `$${Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

/**
 * Returns the delta color class based on percentage difference.
 * Green if within +/-10%, yellow within +/-20%, red beyond.
 */
const getDeltaClass = (percent) => {
  const abs = Math.abs(percent);
  if (abs <= 10) return 'delta-good';
  if (abs <= 20) return 'delta-warn';
  return 'delta-bad';
};

/**
 * Format a delta with sign prefix: +$123.45 or -$123.45
 */
const formatDelta = (value) => {
  if (value == null || isNaN(value)) return '-';
  const abs = Math.abs(Number(value));
  const formatted = `$${abs.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
  return value >= 0 ? `+${formatted}` : `-${formatted}`;
};

/**
 * Format delta percentage with sign: +11% or -9%
 */
const formatDeltaPercent = (percent) => {
  if (percent == null || isNaN(percent)) return '';
  const rounded = Math.round(percent);
  return percent >= 0 ? `(+${rounded}%)` : `(${rounded}%)`;
};

/**
 * EstimateComparison — shows estimate vs actual freight cost comparison.
 * Only renders the comparison table when actual totals exist.
 *
 * Props:
 *   - shipmentId: UUID of the shipment
 */
const EstimateComparison = ({ shipmentId }) => {
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!shipmentId) return;

    const fetchEstimate = async () => {
      try {
        setLoading(true);
        const result = await estimateAPI.getEstimate(shipmentId);
        setEstimate(result.data || null);
      } catch (err) {
        if (err.response?.status === 404) {
          setEstimate(null);
        } else {
          setError('Failed to load estimate comparison');
          console.error('EstimateComparison fetch error:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEstimate();
  }, [shipmentId]);

  // Don't render anything if loading, errored, no estimate, or no actuals
  if (loading) return null;
  if (error) return null;
  if (!estimate) return null;

  // Only show when actualTotal is populated
  const actual = estimate.actual || {};
  if (actual.total == null && estimate.actualTotal == null) return null;

  const breakdown = estimate.breakdown || {};
  const actualTotal = actual.total ?? estimate.actualTotal;

  // Build comparison rows: [label, estimatedValue, actualValue]
  const rows = [];

  // Ocean freight
  const estOcean = breakdown.oceanFreight;
  const actOcean = actual.oceanFreight;
  if (estOcean != null || actOcean != null) {
    const delta = (actOcean != null && estOcean != null) ? actOcean - estOcean : null;
    const pct = (estOcean && delta != null) ? (delta / estOcean) * 100 : null;
    rows.push({ label: 'Ocean', estimated: estOcean, actual: actOcean, delta, percent: pct });
  }

  // Trucking (combined origin + destination)
  const estTrucking = (breakdown.originTrucking || 0) + (breakdown.destinationTrucking || 0);
  const actTrucking = (actual.originTrucking != null || actual.destinationTrucking != null)
    ? (actual.originTrucking || 0) + (actual.destinationTrucking || 0)
    : actual.trucking;
  if (estTrucking > 0 || actTrucking != null) {
    const delta = (actTrucking != null && estTrucking) ? actTrucking - estTrucking : null;
    const pct = (estTrucking && delta != null) ? (delta / estTrucking) * 100 : null;
    rows.push({ label: 'Trucking', estimated: estTrucking || null, actual: actTrucking, delta, percent: pct });
  }

  // Duties
  const estDuties = breakdown.duties;
  const actDuties = actual.duties;
  if (estDuties != null || actDuties != null) {
    const delta = (actDuties != null && estDuties != null) ? actDuties - estDuties : null;
    const pct = (estDuties && delta != null) ? (delta / estDuties) * 100 : null;
    rows.push({ label: 'Duties', estimated: estDuties, actual: actDuties, delta, percent: pct });
  }

  // Total
  const estTotal = estimate.totalEstimated || estimate.total;
  const totalDelta = (actualTotal != null && estTotal != null) ? actualTotal - estTotal : null;
  const totalPct = (estTotal && totalDelta != null) ? (totalDelta / estTotal) * 100 : null;

  return (
    <div className="detail-section estimate-comparison">
      <h2>Estimate vs Actual</h2>

      <table className="simple-table">
        <thead>
          <tr>
            <th></th>
            <th>Estimate</th>
            <th>Actual</th>
            <th>Delta</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <td style={{ fontWeight: 500 }}>{row.label}</td>
              <td>{formatCurrency(row.estimated)}</td>
              <td>{formatCurrency(row.actual)}</td>
              <td>
                {row.delta != null ? (
                  <span className={`estimate-delta ${getDeltaClass(row.percent)}`}>
                    {formatDelta(row.delta)} {formatDeltaPercent(row.percent)}
                  </span>
                ) : '-'}
              </td>
            </tr>
          ))}
          {/* Total row */}
          <tr className="estimate-total-row">
            <td style={{ fontWeight: 600 }}>Total</td>
            <td style={{ fontWeight: 600 }}>{formatCurrency(estTotal)}</td>
            <td style={{ fontWeight: 600 }}>{formatCurrency(actualTotal)}</td>
            <td>
              {totalDelta != null ? (
                <span className={`estimate-delta ${getDeltaClass(totalPct)}`} style={{ fontWeight: 600 }}>
                  {formatDelta(totalDelta)} {formatDeltaPercent(totalPct)}
                </span>
              ) : '-'}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default EstimateComparison;
